import { useState } from 'react'
import {
  useCommissionReviewerUsers,
  useCommissionAccess,
  useCommissionAttributeDeal,
  useCommissionAttributeSurgery,
} from '../../shared/api/generated/commercial-commissions/commercial-commissions'
import type { CommissionAttributionOut } from '../../shared/api/generated/model'
import { dateInClinic, errorMessage } from './helpers'
import { useInvalidateCommissions } from './hooks'

const REASON_MAX = 500

/**
 * Campos e gravação da correção de atribuição. Usado dentro do painel
 * lateral da parcela e do diálogo do card do deal.
 */
export function AttributionForm({
  attribution,
  onSaved,
  onCancel,
  idPrefix = 'cc-attr',
}: {
  attribution: CommissionAttributionOut
  onSaved: () => void
  onCancel?: () => void
  idPrefix?: string
}) {
  const access = useCommissionAccess()
  const users = useCommissionReviewerUsers()
  const deal = useCommissionAttributeDeal()
  const surgery = useCommissionAttributeSurgery()
  const invalidate = useInvalidateCommissions()
  const [originator, setOriginator] = useState(String(attribution.originator_user_id ?? ''))
  const [closer, setCloser] = useState(String(attribution.closer_user_id ?? ''))
  const [date, setDate] = useState(dateInClinic(attribution.closed_at))
  const [reason, setReason] = useState('')
  const [error, setError] = useState('')
  const busy = deal.isPending || surgery.isPending
  const save = async () => {
    if (reason.trim().length < 3) {
      setError('Informe o motivo da correção.')
      return
    }
    const data = {
      originator_user_id: originator ? Number(originator) : null,
      closer_user_id: closer ? Number(closer) : null,
      expected_revision: attribution.revision,
      reason: reason.trim(),
      ...(!attribution.proposal_id && access.data?.can_manage
        ? { closed_at: date ? `${date}T12:00:00-03:00` : null }
        : {}),
    }
    try {
      if (attribution.proposal_id)
        await deal.mutateAsync({ proposalId: attribution.proposal_id, data })
      else if (attribution.legacy_surgery_id)
        await surgery.mutateAsync({ surgeryId: attribution.legacy_surgery_id, data })
      await invalidate()
      onSaved()
    } catch (e) {
      setError(errorMessage(e))
    }
  }
  return (
    <form
      className="cc-form"
      onSubmit={(e) => {
        e.preventDefault()
        void save()
      }}
    >
      <div className="cc-form-grid">
        {[
          {
            id: `${idPrefix}-originator`,
            label: 'Originador',
            value: originator,
            set: setOriginator,
          },
          { id: `${idPrefix}-closer`, label: 'Fechador', value: closer, set: setCloser },
        ].map((f) => (
          <label className="cc-field" key={f.id} htmlFor={f.id}>
            {f.label}
            <select id={f.id} value={f.value} onChange={(e) => f.set(e.target.value)}>
              <option value="">Não identificado</option>
              {users.data?.map((u) => (
                <option key={u.user_id} value={u.user_id}>
                  {u.name}
                  {!u.user_active ? ' (inativo)' : ''}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>
      {!attribution.proposal_id && access.data?.can_manage && (
        <label className="cc-field" htmlFor={`${idPrefix}-date`}>
          Data da venda
          <input
            id={`${idPrefix}-date`}
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <small>
            Se desconhecida, a parcela de venda fica pendente. A operação usa a data da cirurgia.
          </small>
        </label>
      )}
      <label className="cc-field" htmlFor={`${idPrefix}-reason`}>
        <span className="cc-field-label">
          Motivo
          <span className="cc-field-counter">
            {reason.length}/{REASON_MAX}
          </span>
        </span>
        <textarea
          id={`${idPrefix}-reason`}
          value={reason}
          maxLength={REASON_MAX}
          rows={3}
          placeholder="Ex.: fechamento registrado no CRM em nome de outro comercial"
          onChange={(e) => {
            setReason(e.target.value)
            if (error) setError('')
          }}
        />
        <small>Fica registrado no histórico da atribuição.</small>
      </label>
      {users.isError && (
        <p className="cc-notice cc-error" role="alert">
          {errorMessage(users.error)}
        </p>
      )}
      {error && (
        <p className="cc-notice cc-error" role="alert">
          {error}
        </p>
      )}
      <div className="cc-form-actions">
        {onCancel && (
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            Cancelar
          </button>
        )}
        <button
          type="submit"
          className="btn btn-primary"
          disabled={busy || users.isLoading || users.isError}
        >
          {busy ? 'Salvando…' : 'Salvar atribuição'}
        </button>
      </div>
    </form>
  )
}
